import React, { useState } from "react";
import { Send, User, Phone, Mail, MessageSquare, CheckCircle } from "lucide-react";
import "./styles/ContactSection.css";
import ReCAPTCHA from "react-google-recaptcha";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { functions } from "../firebase"; 
import { httpsCallable } from "firebase/functions";

export default function EnquireSection() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "",
    message: ""
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const captchaRef = React.useRef(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.phone || !formData.email || !formData.message) {
      return setErrorMessage("Please fill all required fields.");
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      return setErrorMessage("Enter a valid email address.");
    }

    const captchaToken = captchaRef.current.getValue();
    if (!captchaToken) return setErrorMessage("Please verify reCAPTCHA");
    const verifyRecaptcha = httpsCallable(functions, "verifyRecaptcha");
    const result = await verifyRecaptcha({ token: captchaToken });

    if (!result.data.success) {
      alert("reCAPTCHA failed. Please try again.");
      return;
    }

    setErrorMessage("");
    setLoading(true);

    try {
      await addDoc(collection(db, "enquiries"), {
        ...formData,
        createdAt: serverTimestamp()
      });

      setIsSubmitted(true);

      setFormData({ name:"", phone:"", email:"", subject:"", message:"" });
      captchaRef.current.reset();

      setTimeout(() => setIsSubmitted(false), 3000);

    } catch (err) {
      setErrorMessage("Something went wrong — try again.");
    }

    setLoading(false);
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   console.log("Form submitted:", formData);
  //   setIsSubmitted(true);
    
  //   // Reset form after 3 seconds
  //   setTimeout(() => {
  //     setFormData({
  //       name: "",
  //       phone: "",
  //       email: "",
  //       subject: "",
  //       message: ""
  //     });
  //     setIsSubmitted(false);
  //   }, 3000);
  // };

  return (
    <section className="enquire-section" id="contact">
      {/* Animated Background Elements */}
      <div className="bg-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
      </div>

      <div className="enquire-container">
        {/* Left Side - Info */}
        <div className="enquire-info">
          <div className="info-content">
            <span className="info-badge">Get In Touch</span>
            <h2 className="enquire-title">Enquire Us</h2>
            <p className="enquire-description">
              Have questions about our programs? We're here to help you achieve your educational goals. 
              Fill out the form and our team will get back to you within 24 hours.
            </p>
            
            <div className="info-cards">
              <div className="info-card">
                <div className="info-icon">
                  <Phone size={24} />
                </div>
                <div className="info-text">
                  <h4>Call Us</h4>
                  <p>+61 410 338 620</p>
                </div>
              </div>
              
              <div className="info-card">
                <div className="info-icon">
                  <Mail size={24} />
                </div>
                <div className="info-text">
                  <h4>Email Us</h4>
                  <p>info@ozzieducationgroup.vic.edu.au</p>
                </div>
              </div>
            </div>

            <div className="trust-indicators">
              <div className="trust-item">
                <CheckCircle size={20} />
                <span>Quick Response</span>
              </div>
              <div className="trust-item">
                <CheckCircle size={20} />
                <span>Expert Guidance</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="enquire-form-wrapper">
          <div className="form-card">
            {!isSubmitted ? (
              <>
                <div className="form-header">
                  <h3>Send us a Message</h3>
                  <p>We'll respond as soon as possible</p>
                </div>

                <div className="form-body">
                  <div className="form-group">
                    <label className={`form-label ${focusedField === 'name' || formData.name ? 'active' : ''}`}>
                      Full Name *
                    </label>
                    <div className="input-wrapper">
                      <User className="input-icon" size={20} />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('name')}
                        onBlur={() => setFocusedField(null)}
                        required
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className={`form-label ${focusedField === 'phone' || formData.phone ? 'active' : ''}`}>
                        Phone Number *
                      </label>
                      <div className="input-wrapper">
                        <Phone className="input-icon" size={20} />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('phone')}
                          onBlur={() => setFocusedField(null)}
                          required
                          className="form-input"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className={`form-label ${focusedField === 'email' || formData.email ? 'active' : ''}`}>
                        Email Address *
                      </label>
                      <div className="input-wrapper">
                        <Mail className="input-icon" size={20} />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('email')}
                          onBlur={() => setFocusedField(null)}
                          required
                          className="form-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className={`form-label ${focusedField === 'subject' || formData.subject ? 'active' : ''}`}>
                      Subject
                    </label>
                    <div className="input-wrapper">
                      <MessageSquare className="input-icon" size={20} />
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('subject')}
                        onBlur={() => setFocusedField(null)}
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className={`form-label ${focusedField === 'message' || formData.message ? 'active' : ''}`}>
                      Your Message *
                    </label>
                    <div className="input-wrapper">
                      <MessageSquare className="input-icon textarea-icon" size={20} />
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('message')}
                        onBlur={() => setFocusedField(null)}
                        required
                        className="form-textarea"
                        rows="5"
                      ></textarea>
                    </div>
                  </div>
                  <ReCAPTCHA
                    sitekey="6LekVxgsAAAAAHj-aOW5iC_agmJT3-xhnS3SvWjW"
                    ref={captchaRef}
                    className="captcha-box"
                  />

                  {errorMessage && <p className="form-error">{errorMessage}</p>}

                  <button 
                    type="submit" 
                    className="submit-btn"
                    onClick={handleSubmit}
                  >
                    <span>Send Enquiry</span>
                    <Send size={20} />
                  </button>
                </div>
              </>
            ) : (
              <div className="success-message">
                <div className="success-icon">
                  <CheckCircle size={64} />
                </div>
                <h3>Thank You!</h3>
                <p>Your enquiry has been submitted successfully. We'll get back to you within 24 hours.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}